/**
 * Highlights key terms in text with specified colors
 * Uses brand colors: peach for brand/key terms, mint for features/concepts
 */

// Define highlight patterns
const highlightPatterns = {
  // Brand names - Peach
  brand: {
    terms: ['Welby', 'Health'],
    className: 'highlight-brand'
  },
  // Important features/concepts - Mint
  features: {
    terms: ['UME Health', 'secure', 'HIPAA'],
    className: 'highlight-feature'
  },
  // Action words - Peach
  actions: {
    terms: ['simplifies', 'guides', 'ensures', 'never', 'anywhere'],
    className: 'highlight-action'
  }
};

/**
 * Highlight key terms in text
 * @param {string} text - The text to highlight
 * @returns {Array} Array of text nodes and span elements
 */
export const highlightText = (text) => {
  if (!text) return text;

  let result = [];
  let lastIndex = 0;
  const matches = [];

  // Collect all matches with their positions
  Object.values(highlightPatterns).forEach(pattern => {
    pattern.terms.forEach(term => {
      const regex = new RegExp(`\\b${term}\\b`, 'gi');
      let match;
      while ((match = regex.exec(text)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          term: match[0],
          className: pattern.className
        });
      }
    });
  });

  // Sort matches by position
  matches.sort((a, b) => a.start - b.start);

  // Remove overlapping matches (keep the first one)
  const filteredMatches = [];
  for (let match of matches) {
    if (!filteredMatches.some(m => 
      (match.start >= m.start && match.start < m.end) ||
      (match.end > m.start && match.end <= m.end)
    )) {
      filteredMatches.push(match);
    }
  }

  // Build result with highlighted terms
  filteredMatches.forEach((match, idx) => {
    if (match.start > lastIndex) {
      result.push(text.substring(lastIndex, match.start));
    }
    result.push(
      `<span class="${match.className}">${match.term}</span>`
    );
    lastIndex = match.end;
  });

  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }

  return result.join('');
};

/**
 * Highlight text in JSX - returns array of JSX elements
 * @param {string} text - The text to highlight
 * @returns {Array} Array of strings and JSX spans
 */
export const highlightTextJSX = (text) => {
  if (!text) return text;

  let result = [];
  let lastIndex = 0;
  const matches = [];

  // Collect all matches with their positions
  Object.values(highlightPatterns).forEach(pattern => {
    pattern.terms.forEach(term => {
      const regex = new RegExp(`\\b${term}\\b`, 'gi');
      let match;
      while ((match = regex.exec(text)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          term: match[0],
          className: pattern.className
        });
      }
    });
  });

  // Sort matches by position
  matches.sort((a, b) => a.start - b.start);

  // Remove overlapping matches (keep the first one)
  const filteredMatches = [];
  for (let match of matches) {
    if (!filteredMatches.some(m => 
      (match.start >= m.start && match.start < m.end) ||
      (match.end > m.start && match.end <= m.end)
    )) {
      filteredMatches.push(match);
    }
  }

  // Build result with highlighted terms
  let resultIndex = 0;
  filteredMatches.forEach((match, idx) => {
    if (match.start > lastIndex) {
      result.push(
        <span key={`text-${idx}`}>{text.substring(lastIndex, match.start)}</span>
      );
    }
    result.push(
      <span key={`highlight-${idx}`} className={match.className}>{match.term}</span>
    );
    lastIndex = match.end;
    resultIndex++;
  });

  if (lastIndex < text.length) {
    result.push(
      <span key={`text-end`}>{text.substring(lastIndex)}</span>
    );
  }

  return result.length > 0 ? result : text;
};
